import { Box, Flex, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { Comic, Release } from "../../types";

interface FeedComicInfoCardProps {
  comic: Comic;
  release?: Release;
  // Optional action affordance (like + share). Rendered on the right edge
  // of the gradient block so it sits alongside the title instead of
  // overlapping it.
  actions?: ReactNode;
}

/**
 * Title + category overlay at the bottom of a comic feature slot. No card
 * backing — just a transparent-to-dark gradient that ensures the text reads
 * cleanly over busy artwork. Matches the design language of the mocks:
 * orange uppercase tag, bold white title, modest subtitle row.
 */
export function FeedComicInfoCard({ comic, release, actions }: FeedComicInfoCardProps) {
  const chapterLabel =
    release?.chapter && release.chapter > 0
      ? `Chapter ${release.chapter}`
      : null;
  const tag = comic.category_name ?? null;

  return (
    <Flex
      position="absolute"
      left={0}
      right={0}
      bottom={0}
      align="flex-end"
      gap={3}
      color="white"
      px={5}
      pt={12}
      pb={5}
      // Gradient itself is non-interactive so the deck swipe still works
      // through the empty parts of the overlay; the actions slot below
      // re-enables pointer events for itself.
      pointerEvents="none"
      zIndex={1500}
      sx={{
        background:
          "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 35%, rgba(0,0,0,0.92) 100%)",
      }}
    >
      <Flex direction="column" gap={1} flex="1" minW="0">
        {tag && (
          <Text
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="orange.400"
            mb={1}
          >
            {tag}
          </Text>
        )}
        <Text
          fontSize="2xl"
          fontWeight="bold"
          lineHeight="1.1"
          noOfLines={2}
          sx={{ textShadow: "0 2px 8px rgba(0,0,0,0.55)" }}
        >
          {comic.name}
        </Text>
        {(comic.artist?.name || chapterLabel) && (
          <Flex
            align="center"
            gap={2}
            fontSize="sm"
            opacity={0.85}
            wrap="wrap"
            mt={1}
          >
            {comic.artist?.name && <Text>{comic.artist.name}</Text>}
            {comic.artist?.name && chapterLabel && <Text opacity={0.5}>·</Text>}
            {chapterLabel && (
              <Text>
                {chapterLabel}
                {release?.title ? ` · ${release.title}` : ""}
              </Text>
            )}
          </Flex>
        )}
      </Flex>
      {actions ? (
        <Box flexShrink={0} pointerEvents="auto" pb={1}>
          {actions}
        </Box>
      ) : null}
    </Flex>
  );
}
